import { HUDDLE_MAX, HUDDLE_TEXT, huddleErrorMessage } from './huddleApi';
import type { RecordingState } from './uploadQueue';

/**
 * The call's state machine (h1-plan.md "States", :229-238). Pure, so every
 * transition is a unit test. Effects (mic, rpcs, channel, recorder) live in
 * HuddleProvider and only ever dispatch these events.
 */

export type CallPhase =
  | 'idle'
  | 'joining'
  | 'in_call'
  | 'leaving'
  | 'full'
  | 'mic_denied'
  | 'no_mic'
  | 'replaced'
  | 'connection_lost'
  | 'error';

export interface CallState {
  phase: CallPhase;
  /** The room the call (or the failed attempt) belongs to. */
  roomId: string | null;
  roomName: string;
  huddleId: string | null;
  participantId: string | null;
  max: number;
  /** Date.now() when the seat was taken, for the bar's timer. */
  joinedAt: number | null;
  muted: boolean;
  recording: RecordingState;
  /** The one line under the button / in the bar, as plain text. */
  message: string | null;
}

export type CallEvent =
  | { type: 'join_start'; roomId: string; roomName: string }
  | { type: 'mic_denied' }
  | { type: 'mic_missing' }
  | { type: 'relay_unavailable' }
  | { type: 'join_failed'; message: string }
  | {
      type: 'joined';
      huddleId: string;
      participantId: string;
      max: number;
      joinedAt: number;
    }
  | { type: 'muted'; muted: boolean }
  | { type: 'recording'; state: RecordingState }
  | { type: 'channel_lost' }
  | { type: 'seat_lost' }
  | { type: 'leave_start' }
  | { type: 'left' }
  | { type: 'dismiss' };

export const INITIAL_CALL: CallState = {
  phase: 'idle',
  roomId: null,
  roomName: '',
  huddleId: null,
  participantId: null,
  max: HUDDLE_MAX,
  joinedAt: null,
  muted: false,
  recording: 'recording',
  message: null,
};

/** Phases that hold (or are taking, or are giving up) a seat. */
export function isActive(phase: CallPhase): boolean {
  return phase === 'joining' || phase === 'in_call' || phase === 'leaving';
}

export function callReducer(state: CallState, event: CallEvent): CallState {
  switch (event.type) {
    case 'relay_unavailable':
      return isActive(state.phase)
        ? {
            ...state,
            message:
              'Relay unavailable. People on other networks may not connect. Leave and try again.',
          }
        : state;
    case 'join_start':
      if (isActive(state.phase)) return state;
      return {
        ...INITIAL_CALL,
        phase: 'joining',
        roomId: event.roomId,
        roomName: event.roomName,
      };
    case 'mic_denied':
      if (state.phase !== 'joining') return state;
      return { ...state, phase: 'mic_denied', message: HUDDLE_TEXT.micDenied };
    case 'mic_missing':
      if (state.phase !== 'joining') return state;
      return { ...state, phase: 'no_mic', message: HUDDLE_TEXT.noMic };
    case 'join_failed': {
      if (state.phase !== 'joining') return state;
      const full = event.message.includes('huddle_full');
      return {
        ...state,
        phase: full ? 'full' : 'error',
        message: huddleErrorMessage(event.message, state.max),
      };
    }
    case 'joined':
      if (state.phase !== 'joining') return state;
      return {
        ...state,
        phase: 'in_call',
        huddleId: event.huddleId,
        participantId: event.participantId,
        max: event.max,
        joinedAt: event.joinedAt,
        recording: 'recording',
        message: null,
      };
    case 'muted':
      return isActive(state.phase) ? { ...state, muted: event.muted } : state;
    case 'recording':
      if (!isActive(state.phase)) return state;
      // The not-recorded line only clears on a fresh join (plan 2.4).
      if (state.recording === 'not_recorded') return state;
      return { ...state, recording: event.state };
    case 'channel_lost':
      if (state.phase !== 'in_call') return state;
      return { ...state, phase: 'connection_lost', message: HUDDLE_TEXT.connectionLost };
    case 'seat_lost':
      if (state.phase !== 'in_call') return state;
      return { ...state, phase: 'replaced', message: huddleErrorMessage('not_in_huddle') };
    case 'leave_start':
      if (state.phase !== 'in_call') return state;
      return { ...state, phase: 'leaving' };
    case 'left':
      return { ...INITIAL_CALL };
    case 'dismiss':
      return isActive(state.phase) ? state : { ...INITIAL_CALL };
  }
}

/** The bar's recording line (plan 0.2, 2.6). */
export function recordingLine(recording: RecordingState): string {
  if (recording === 'not_recorded') return HUDDLE_TEXT.notRecorded;
  if (recording === 'paused') return HUDDLE_TEXT.paused;
  return HUDDLE_TEXT.inCall;
}

/** The Start / Join / Full label (h1-plan.md:219). */
export function startButtonLabel(
  live: { activeCount: number } | undefined,
  max = HUDDLE_MAX,
): string {
  if (live === undefined) return 'Start huddle';
  if (live.activeCount >= max) return `Full (${max} of ${max})`;
  return `Join huddle (${live.activeCount} of ${max})`;
}

/** mm:ss, or h:mm:ss past the hour, for the bar's timer. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${two(m)}:${two(s)}`;
}
